import React from 'react';
import { JobNoteItem } from './JobNoteItem';
import { JobNoteForm } from './JobNoteForm';
import * as jobsApi from '@/lib/api/jobs';
import { useAuth } from '@/lib/hooks/useAuth';

interface JobNotesListProps {
  jobId: string;
}

export const JobNotesList: React.FC<JobNotesListProps> = ({ jobId }) => {
  const { user } = useAuth();
  const [notes, setNotes] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  const loadNotes = React.useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const data = await jobsApi.getJobNotes(jobId);
      setNotes(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err.message || 'Failed to load notes');
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  React.useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  const handleAddNote = async (note: string, noteType: string, isPinned: boolean) => {
    try {
      await jobsApi.createJobNote(jobId, note, user?.id?.toString(), noteType as any, isPinned);
      await loadNotes();
    } catch (err: any) {
      setError(err.message || 'Failed to add note');
      throw err;
    }
  };

  const handleEditNote = async (noteId: number, note: string, noteType: string, isPinned: boolean) => {
    try {
      await jobsApi.updateJobNote(jobId, noteId.toString(), { note, note_type: noteType, is_pinned: isPinned });
      await loadNotes();
    } catch (err: any) {
      setError(err.message || 'Failed to update note');
    }
  };

  const handleDeleteNote = async (noteId: number) => {
    try {
      await jobsApi.deleteJobNote(jobId, noteId.toString());
      await loadNotes();
    } catch (err: any) {
      setError(err.message || 'Failed to delete note');
    }
  };

  const handleTogglePin = async (noteId: number, isPinned: boolean) => {
    try {
      const note = notes.find(n => n.id === noteId);
      if (note) {
        await jobsApi.updateJobNote(jobId, noteId.toString(), {
          note: note.note,
          note_type: note.note_type,
          is_pinned: isPinned
        });
        await loadNotes();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to pin note');
    }
  };

  // Separate pinned and unpinned notes
  const pinnedNotes = notes.filter(note => note.is_pinned);
  const unpinnedNotes = notes.filter(note => !note.is_pinned);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center gap-3 text-gray-400">
          <span className="material-symbols-outlined animate-spin">progress_activity</span>
          <span>Loading notes...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Message */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-400">
            <span className="material-symbols-outlined">error</span>
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Add Note Form */}
      <JobNoteForm onSubmit={handleAddNote} />

      {/* Notes List */}
      {notes.length === 0 ? (
        <div className="text-center py-12">
          <span className="material-symbols-outlined text-gray-600 text-5xl mb-4">
            note_add
          </span>
          <p className="text-gray-400">No notes yet. Add the first note above!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Pinned Notes Section */}
          {pinnedNotes.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium text-blue-400">
                <span className="material-symbols-outlined text-base">push_pin</span>
                <span>Pinned Notes</span>
              </div>
              {pinnedNotes.map(note => (
                <JobNoteItem
                  key={note.id}
                  note={note}
                  onEdit={handleEditNote}
                  onDelete={handleDeleteNote}
                  onTogglePin={handleTogglePin}
                />
              ))}
            </div>
          )}

          {/* Regular Notes Section */}
          {unpinnedNotes.length > 0 && (
            <div className="space-y-4">
              {pinnedNotes.length > 0 && (
                <div className="flex items-center gap-2 text-sm font-medium text-gray-400">
                  <span className="material-symbols-outlined text-base">notes</span>
                  <span>All Notes</span>
                </div>
              )}
              {unpinnedNotes.map(note => (
                <JobNoteItem
                  key={note.id}
                  note={note}
                  onEdit={handleEditNote}
                  onDelete={handleDeleteNote}
                  onTogglePin={handleTogglePin}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

